/**
 * <s-gallery-scroller> — one-photo-at-a-time product gallery.
 *
 * Required markup:
 *   [data-gallery-track]  — horizontally scrolling, snap container.
 *   [data-gallery-slide]  — each slide inside the track.
 *   [data-gallery-prev] / [data-gallery-next] — optional arrow buttons.
 *   [data-gallery-thumb][data-index] — optional thumbnail buttons.
 */
class SGalleryScroller extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = [...this.#track.querySelectorAll('[data-gallery-slide]')];

    this.addEventListener('click', this.#handleClick, opts);
    this.#track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #index() {
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  #goTo(index) {
    const clamped = Math.max(0, Math.min(this.#slides.length - 1, index));
    const slide = this.#slides[clamped];
    if (!slide) return;
    const dir = getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
    this.#track.scrollTo({ left: dir * clamped * this.#track.clientWidth });
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-gallery-prev]')) return this.#goTo(this.#index - 1);
    if (event.target.closest('[data-gallery-next]')) return this.#goTo(this.#index + 1);
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) this.#goTo(Number(thumb.dataset.index));
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const index = this.#index;
    const last = this.#slides.length - 1;
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= last;
    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${index + 1} / ${this.#slides.length}`;
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      const active = Number(thumb.dataset.index) === index;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    }
  }
}

if (!customElements.get('s-gallery-scroller')) {
  customElements.define('s-gallery-scroller', SGalleryScroller);
}
